import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Box,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  DirectionsCar as CarIcon,
  NotificationsActive as AlarmIcon,
  Check as CheckIcon,
  Clear as RejectIcon,
  Star as StarIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../contexts/ThemeContext';
import { getFullImageUrl } from '../../utils/helpers';

interface UnifiedNotification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: string; // 'booking_request', 'booking_confirmed', 'booking_approved', 'booking_rejected', 'alarm'
  isRead: boolean;
  data: {
    alarmId?: string;
    rideId?: string;
    bookingId?: string;
    passengerId?: string;
    passengerName?: string;
    passengerPreferredName?: string | null;
    passengerRating?: number;
    passengerPicture?: string | null;
    passengerTrips?: number;
    seatsBooked?: number | null;
    origin?: string;
    destination?: string;
    departureDate?: string;
    departureDateTime?: string;
    specialRequests?: string | null;
    driver?: {
      id: string;
      firstName: string;
      lastName: string;
      preferredName?: string;
      profilePicture?: string;
    };
    originCity?: string;
    destinationCity?: string;
    preferredDateTime?: string;
    passengerCount?: number;
    minBudget?: number | null;
    maxBudget?: number | null;
    currency?: string;
    needsLargeLuggage?: boolean;
    needsChildSeat?: boolean;
    needsWheelchairAccess?: boolean;
    specialRequirements?: string | null;
    description?: string | null;
    requestId?: string;
    senderId?: string;
    isBidRide?: boolean;
    bidId?: string;
    urgency?: 'upcoming' | 'urgent';
  };
  createdAt: string;
}

interface AlarmModalProps {
  open: boolean;
  onClose: () => void;
}

const AlarmModal: React.FC<AlarmModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { actualMode: mode } = useThemeContext();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<UnifiedNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<Record<string, 'approve' | 'reject' | null>>({});
  const [actionSuccess, setActionSuccess] = useState<Record<string, 'approved' | 'rejected'>>({});
  const [bidsState, setBidsState] = useState<Record<string, { price: string; message: string; submitting: boolean; success: boolean; error: string }>>({});

  const handleNewRideRequestBid = async (notificationId: string, requestId: string) => {
    const state = bidsState[notificationId] || { price: '', message: '', submitting: false, success: false, error: '' };
    const price = parseFloat(state.price);
    if (isNaN(price) || price <= 0) {
      setBidsState(prev => ({
        ...prev,
        [notificationId]: { ...state, error: t('searchResults.requestDetails.priceError', 'Please enter a valid price') }
      }));
      return;
    }

    setBidsState(prev => ({
      ...prev,
      [notificationId]: { ...state, submitting: true, error: '' }
    }));

    try {
      const response = await apiService.post('/bids', {
        requestId,
        priceOffer: price,
        message: state.message.trim() || undefined
      });

      if (response.success) {
        setBidsState(prev => ({
          ...prev,
          [notificationId]: { price: '', message: '', submitting: false, success: true, error: '' }
        }));
        // Delete/dismiss the notification from the DB automatically since the action is completed
        await apiService.delete(`/notifications/${notificationId}`);
        // Remove from local list after a brief delay so the success state can be seen
        setTimeout(() => {
          setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
        }, 1500);
      } else {
        setBidsState(prev => ({
          ...prev,
          [notificationId]: { ...state, submitting: false, error: response.message || 'Failed to submit bid' }
        }));
      }
    } catch (err: any) {
      console.error('Submit notification bid error:', err);
      setBidsState(prev => ({
        ...prev,
        [notificationId]: {
          ...state,
          submitting: false,
          error: err.response?.data?.message || 'Failed to submit bid'
        }
      }));
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: any = await apiService.get('/notifications');
      if (response.success) {
        setNotifications(response.data || []);
      } else {
        setError(t('alarmModal.error', 'Failed to load notifications'));
      }
    } catch (err: any) {
      console.error('Error fetching notifications:', err);
      setError(err.response?.data?.error || t('alarmModal.error', 'Failed to load notifications'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open]);

  const handleNotificationClick = async (item: UnifiedNotification) => {
    if (!item.isRead) {
      try {
        await apiService.put(`/notifications/${item.id}/read`);
        setNotifications(prev =>
          prev.map(n => n.id === item.id ? { ...n, isRead: true } : n)
        );
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }

    onClose();

    const data = item.data || {} as any;
    if (item.type === 'new_ride_request' && data.requestId) {
      navigate(`/browse-requests?id=${data.requestId}`);
    } else if (item.type === 'chat' && data.senderId) {
      navigate(`/inbox/${data.senderId}`);
    } else if (data.isBidRide && data.requestId) {
      navigate(`/browse-requests?id=${data.requestId}`);
    } else if (data.rideId) {
      if (typeof data.rideId === 'string' && data.rideId.startsWith('bid-') && data.requestId) {
        navigate(`/browse-requests?id=${data.requestId}`);
      } else {
        navigate(`/ride/${data.rideId}`);
      }
    } else if (data.requestId) {
      navigate(`/browse-requests?id=${data.requestId}`);
    } else if (data.passengerId) {
      navigate(`/inbox/${data.passengerId}`);
    } else {
      navigate('/my-rides');
    }
  };

  const handleDeleteNotification = async (id: string) => {
    try {
      const response: any = await apiService.delete(`/notifications/${id}`);
      if (response.success) {
        setNotifications(prev => prev.filter(notif => notif.id !== id));
      } else {
        alert(t('alarmModal.deleteError', 'Failed to dismiss notification'));
      }
    } catch (err: any) {
      console.error('Error deleting notification:', err);
      alert(t('alarmModal.deleteError', 'Failed to dismiss notification'));
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const response: any = await apiService.put('/notifications/read-all');
      if (response.success) {
        fetchNotifications();
      }
    } catch (err: any) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const handleDismissAll = async () => {
    if (window.confirm(t('notifications.confirmDismissAll', 'Are you sure you want to dismiss all notifications?'))) {
      try {
        const response: any = await apiService.delete('/notifications/delete-all');
        if (response.success) {
          setNotifications([]);
        } else {
          alert(t('alarmModal.clearAllError', 'Failed to dismiss all notifications'));
        }
      } catch (err: any) {
        console.error('Error dismissing all notifications:', err);
        alert(t('alarmModal.clearAllError', 'Failed to dismiss all notifications'));
      }
    }
  };

  const handleBookingAction = async (notificationId: string, bookingId: string, action: 'approve' | 'reject') => {
    setActionLoading(prev => ({ ...prev, [notificationId]: action }));
    try {
      const response: any = await apiService.put(`/bookings/${bookingId}/approve`, { action });
      if (response.success) {
        setActionSuccess(prev => ({ ...prev, [notificationId]: action === 'approve' ? 'approved' : 'rejected' }));
        // Delete the notification from the DB automatically since the action is completed
        await apiService.delete(`/notifications/${notificationId}`);
        // Remove the notification from the list state so it disappears
        setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      } else {
        alert(response.message || 'Action failed');
      }
    } catch (err: any) {
      console.error(`Booking ${action} error:`, err);
      alert(err.response?.data?.message || `Failed to ${action} booking request`);
    } finally {
      setActionLoading(prev => ({ ...prev, [notificationId]: null }));
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM dd, yyyy • h:mm a');
    } catch {
      return dateString;
    }
  };

  const getTimeAgo = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return t('notifications.justNow', 'Just now');
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    } catch {
      return '';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '32px',
          maxHeight: '85vh',
          background: mode === 'dark' ? 'rgba(20, 20, 20, 0.85)' : 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(25px)',
          border: '1px solid',
          borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
        p: { xs: 3, md: 4 },
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AlarmIcon sx={{ fontSize: '3rem' }} />
          <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
            {t('notifications.title', 'Notifications & Alarms')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {notifications.length > 0 && (
            <>
              <Button
                onClick={handleMarkAllRead}
                variant="text"
                sx={{
                  color: 'white',
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  px: 2,
                  borderRadius: '12px',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                }}
              >
                {t('notifications.markAllRead', 'Mark all read')}
              </Button>
              <Button
                onClick={handleDismissAll}
                variant="text"
                sx={{
                  color: 'white',
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  px: 2,
                  borderRadius: '12px',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                }}
              >
                {t('notifications.dismissAll', 'Dismiss all')}
              </Button>
            </>
          )}
          <IconButton onClick={onClose} sx={{ color: 'white', backgroundColor: 'rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}>
            <CloseIcon sx={{ fontSize: '2rem' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: { xs: 2, md: 4 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={64} sx={{ color: '#F59E0B' }} />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            sx={{
              fontSize: '1.5rem',
              borderRadius: '24px',
              p: 3,
              '& .MuiAlert-icon': { fontSize: '2.5rem' }
            }}
          >
            {error}
          </Alert>
        ) : notifications.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Box sx={{
              display: 'inline-flex',
              p: 4,
              borderRadius: '50%',
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
              mb: 3
            }}>
              <AlarmIcon sx={{ fontSize: '6rem', color: 'text.secondary', opacity: 0.5 }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', mb: 2 }}>
              {t('notifications.noNotifications', 'No notifications yet')}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: '400px', mx: 'auto', lineHeight: 1.5 }}>
              {t('notifications.noNotificationsSub', 'We will alert you when there are updates on your rides.')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 0 }}>
            {notifications.map((item) => {
              const isRequest = item.type === 'booking_request';
              const isNewRideRequest = item.type === 'new_ride_request';
              const isAlarm = item.type === 'alarm';
              const isApproved = actionSuccess[item.id] === 'approved' || item.type === 'booking_approved';
              const isRejected = actionSuccess[item.id] === 'rejected' || item.type === 'booking_rejected';

              const urgency = item.data?.urgency;
              const isUrgentAlarm = isAlarm && urgency === 'urgent';
              const isUpcomingAlarm = isAlarm && urgency === 'upcoming';

              return (
                <Paper
                  key={item.id}
                  elevation={0}
                  onClick={() => handleNotificationClick(item)}
                  sx={{
                    borderRadius: '24px',
                    backgroundColor: isUrgentAlarm
                      ? (mode === 'dark' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.04)')
                      : isUpcomingAlarm
                      ? (mode === 'dark' ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.04)')
                      : (mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: '1px solid',
                    borderColor: isRequest
                      ? 'rgba(139, 92, 246, 0.2)'
                      : isNewRideRequest
                      ? 'rgba(59, 130, 246, 0.2)'
                      : isUrgentAlarm
                      ? 'rgba(239, 68, 68, 0.3)'
                      : isUpcomingAlarm
                      ? 'rgba(59, 130, 246, 0.3)'
                      : 'transparent',
                    cursor: 'pointer',
                    opacity: item.isRead ? 0.75 : 1,
                    '&:hover': {
                      backgroundColor: isUrgentAlarm
                        ? (mode === 'dark' ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.08)')
                        : isUpcomingAlarm
                        ? (mode === 'dark' ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)')
                        : (mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
                      transform: 'translateY(-4px)',
                      borderColor: isRequest
                        ? '#8B5CF6'
                        : isNewRideRequest
                        ? '#3B82F6'
                        : isUrgentAlarm
                        ? '#EF4444'
                        : isUpcomingAlarm
                        ? '#3B82F6'
                        : 'rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <ListItem
                    sx={{ p: 4, flexDirection: 'column', alignItems: 'stretch' }}
                    secondaryAction={
                      <IconButton
                        onClick={(e) => { e.stopPropagation(); handleDeleteNotification(item.id); }}
                        color="error"
                        sx={{
                          backgroundColor: 'rgba(211, 47, 47, 0.1)',
                          '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.2)' },
                          p: 1.5,
                          alignSelf: 'flex-start'
                        }}
                        title={t('alarmModal.delete', 'Dismiss')}
                      >
                        <DeleteIcon sx={{ fontSize: '1.8rem' }} />
                      </IconButton>
                    }
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, width: '100%' }}>
                      <ListItemAvatar>
                        {isAlarm && item.data.driver ? (
                          <Avatar
                            src={getFullImageUrl(item.data.driver.profilePicture)}
                            sx={{
                              width: 64,
                              height: 64,
                              boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                              border: '2px solid white',
                              bgcolor: isUrgentAlarm ? '#EF4444' : isUpcomingAlarm ? '#3B82F6' : '#F59E0B',
                              fontSize: '1.6rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {(item.data.driver.preferredName || item.data.driver.firstName || '?')[0]}
                          </Avatar>
                        ) : (
                          <Avatar
                            src={(isRequest || isNewRideRequest) ? getFullImageUrl(item.data.passengerPicture) : undefined}
                            sx={{
                              width: 64,
                              height: 64,
                              boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                              border: '2px solid white',
                              bgcolor: isRequest ? '#8B5CF6' : isNewRideRequest ? '#3B82F6' : isUrgentAlarm ? '#EF4444' : isUpcomingAlarm ? '#3B82F6' : '#6B7280',
                              fontSize: '1.6rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {(isRequest || isNewRideRequest) ? (item.data.passengerName || '?')[0] : <AlarmIcon />}
                          </Avatar>
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                            <Typography variant="h4" sx={{ fontWeight: 900, fontSize: '1.6rem' }}>
                              {item.title}
                            </Typography>
                            <Chip
                              label={getTimeAgo(item.createdAt)}
                              size="small"
                              sx={{
                                fontSize: '0.9rem',
                                fontWeight: 700,
                                backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                                color: 'text.secondary'
                              }}
                            />
                            {isRequest && !actionSuccess[item.id] && (
                              <Chip
                                label={t('notifications.pending', 'Pending Approval')}
                                size="small"
                                sx={{
                                  fontSize: '0.9rem',
                                  fontWeight: 800,
                                  backgroundColor: 'rgba(139, 92, 246, 0.15)',
                                  color: '#6D28D9'
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '1.25rem', lineHeight: 1.5, mb: 2 }}>
                              {item.message}
                            </Typography>

                            {/* Additional metadata details */}
                            {isAlarm && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <TimeIcon sx={{ fontSize: '1.3rem', color: '#F59E0B' }} />
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    {t('alarmModal.departure', 'Departure:')} {item.data.departureDateTime ? formatDateTime(item.data.departureDateTime) : ''}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <PersonIcon sx={{ fontSize: '1.3rem', color: '#F59E0B' }} />
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    {t('alarmModal.driver', 'Driver:')} {item.data.driver ? `${item.data.driver.preferredName || item.data.driver.firstName} ${item.data.driver.lastName ? item.data.driver.lastName.charAt(0) + '.' : ''}` : ''}
                                  </Typography>
                                </Box>
                                {item.data.seatsBooked && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <CarIcon sx={{ fontSize: '1.3rem', color: '#F59E0B' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      {t('alarmModal.seatsBooked', 'Seats Booked:')} {item.data.seatsBooked}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}                             {isRequest && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)', p: 2.5, borderRadius: '16px' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: '200px' }}>
                                    <LocationIcon sx={{ fontSize: '1.4rem', color: '#8B5CF6' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.1rem' }}>
                                      {item.data.origin} → {item.data.destination}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: '200px' }}>
                                    <TimeIcon sx={{ fontSize: '1.4rem', color: '#8B5CF6' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '1.1rem' }}>
                                      {item.data.departureDate}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: '200px' }}>
                                    <CarIcon sx={{ fontSize: '1.4rem', color: '#8B5CF6' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '1.1rem' }}>
                                      {t('notifications.seatsRequested', 'Seats Requested:')} <strong>{item.data.seatsBooked}</strong>
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: '200px' }}>
                                    <PersonIcon sx={{ fontSize: '1.4rem', color: '#8B5CF6' }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '1.1rem' }}>
                                        {t('notifications.passenger', 'Passenger:')} <strong>{item.data.passengerName}</strong>
                                      </Typography>
                                      
                                      {/* Rating */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: 'rgba(139, 92, 246, 0.1)', px: 1, py: 0.2, borderRadius: '6px' }}>
                                        <StarIcon sx={{ fontSize: '1.1rem', color: '#8B5CF6' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#6D28D9', fontSize: '0.9rem' }}>
                                          {item.data.passengerRating ? item.data.passengerRating.toFixed(1) : 'New'}
                                        </Typography>
                                      </Box>
 
                                      {/* Trips count */}
                                      <Box sx={{ backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', px: 1, py: 0.2, borderRadius: '6px' }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.9rem' }}>
                                          {item.data.passengerTrips} {item.data.passengerTrips === 1 ? 'trip' : 'trips'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>
 
                                {/* Passenger Message / Special Requests */}
                                {item.data.specialRequests && (
                                  <Box sx={{ 
                                    backgroundColor: mode === 'dark' ? 'rgba(139, 92, 246, 0.05)' : 'rgba(139, 92, 246, 0.03)', 
                                    borderLeft: '4px solid #8B5CF6',
                                    p: 2, 
                                    borderRadius: '8px 16px 16px 8px',
                                    mt: 1
                                  }}>
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '1.1rem', lineHeight: 1.5 }}>
                                      "{item.data.specialRequests}"
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}

                            {isNewRideRequest && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)', p: 2.5, borderRadius: '16px' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: '200px' }}>
                                    <LocationIcon sx={{ fontSize: '1.4rem', color: '#F59E0B' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.1rem' }}>
                                      {item.data.originCity} → {item.data.destinationCity}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: '200px' }}>
                                    <TimeIcon sx={{ fontSize: '1.4rem', color: '#F59E0B' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '1.1rem' }}>
                                      {item.data.preferredDateTime ? formatDateTime(item.data.preferredDateTime) : ''}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: '200px' }}>
                                    <CarIcon sx={{ fontSize: '1.4rem', color: '#F59E0B' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '1.1rem' }}>
                                      {t('notifications.seatsRequested', 'Seats Requested:')} <strong>{item.data.passengerCount}</strong>
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: '200px' }}>
                                    <PersonIcon sx={{ fontSize: '1.4rem', color: '#F59E0B' }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '1.1rem' }}>
                                        {t('notifications.passenger', 'Passenger:')} <strong>{item.data.passengerName}</strong>
                                      </Typography>
                                      
                                      {/* Rating */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: 'rgba(245,158,11,0.1)', px: 1, py: 0.2, borderRadius: '6px' }}>
                                        <StarIcon sx={{ fontSize: '1.1rem', color: '#F59E0B' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#D97706', fontSize: '0.9rem' }}>
                                          {item.data.passengerRating ? item.data.passengerRating.toFixed(1) : 'New'}
                                        </Typography>
                                      </Box>

                                      {/* Trips count */}
                                      <Box sx={{ backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', px: 1, py: 0.2, borderRadius: '6px' }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.9rem' }}>
                                          {item.data.passengerTrips} {item.data.passengerTrips === 1 ? 'trip' : 'trips'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                  {(item.data.minBudget !== null && item.data.minBudget !== undefined || item.data.maxBudget !== null && item.data.maxBudget !== undefined) && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: '200px' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#10B981', fontSize: '1.1rem' }}>
                                        {t('notifications.budget', 'Budget:')} <strong>{item.data.minBudget !== null && item.data.minBudget !== undefined ? `${item.data.minBudget} - ` : ''}{item.data.maxBudget} {item.data.currency || 'CAD'}</strong>
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>

                                {/* Preferences & Requirements */}
                                {(item.data.needsLargeLuggage || item.data.needsChildSeat || item.data.needsWheelchairAccess) && (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {item.data.needsLargeLuggage && (
                                      <Chip label={t('rideRequest.largeLuggage', 'Large Luggage')} size="small" color="primary" variant="outlined" />
                                    )}
                                    {item.data.needsChildSeat && (
                                      <Chip label={t('rideRequest.childSeat', 'Child Seat')} size="small" color="primary" variant="outlined" />
                                    )}
                                    {item.data.needsWheelchairAccess && (
                                      <Chip label={t('rideRequest.wheelchair', 'Wheelchair Access')} size="small" color="primary" variant="outlined" />
                                    )}
                                  </Box>
                                )}

                                {/* Special Requirements */}
                                {item.data.specialRequirements && (
                                  <Box sx={{ 
                                    backgroundColor: mode === 'dark' ? 'rgba(245, 158, 11, 0.05)' : 'rgba(245, 158, 11, 0.03)', 
                                    borderLeft: '4px solid #F59E0B',
                                    p: 2, 
                                    borderRadius: '8px 16px 16px 8px'
                                  }}>
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '1.1rem', lineHeight: 1.5 }}>
                                      "{item.data.specialRequirements}"
                                    </Typography>
                                  </Box>
                                )}

                                {/* Description */}
                                {item.data.description && (
                                  <Box sx={{ p: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '1.1rem' }}>
                                      {item.data.description}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </Box>

                    {/* Driver Confirm/Reject Action Buttons */}
                    {isRequest && (
                      <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', width: '100%', gap: 2, pl: 11 }}>
                        {actionSuccess[item.id] ? (
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1.5,
                            px: 3,
                            borderRadius: '12px',
                            backgroundColor: actionSuccess[item.id] === 'approved' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                            color: actionSuccess[item.id] === 'approved' ? '#10B981' : '#EF4444'
                          }}>
                            {actionSuccess[item.id] === 'approved' ? (
                              <>
                                <CheckIcon sx={{ fontSize: '1.8rem' }} />
                                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                  {t('notifications.approved', 'Confirmed & Booked')}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <RejectIcon sx={{ fontSize: '1.8rem' }} />
                                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                  {t('notifications.rejected', 'Request Rejected')}
                                </Typography>
                              </>
                            )}
                          </Box>
                        ) : (
                          <>
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleBookingAction(item.id, item.data.bookingId!, 'approve'); }}
                              variant="contained"
                              disabled={!!actionLoading[item.id]}
                              startIcon={actionLoading[item.id] === 'approve' ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
                              sx={{
                                py: 1.5,
                                px: 4,
                                fontSize: '1.25rem',
                                fontWeight: 800,
                                borderRadius: '16px',
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                boxShadow: '0 8px 16px rgba(16, 185, 129, 0.25)',
                                color: 'white',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                  transform: 'translateY(-2px)'
                                }
                              }}
                            >
                              {t('notifications.confirm', 'Confirm')}
                            </Button>
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleBookingAction(item.id, item.data.bookingId!, 'reject'); }}
                              variant="outlined"
                              disabled={!!actionLoading[item.id]}
                              startIcon={actionLoading[item.id] === 'reject' ? <CircularProgress size={18} color="inherit" /> : <RejectIcon />}
                              sx={{
                                py: 1.5,
                                px: 4,
                                fontSize: '1.25rem',
                                fontWeight: 800,
                                borderRadius: '16px',
                                textTransform: 'none',
                                borderColor: '#EF4444',
                                color: '#EF4444',
                                '&:hover': {
                                  borderColor: '#B91C1C',
                                  backgroundColor: 'rgba(239, 68, 68, 0.05)',
                                  transform: 'translateY(-2px)'
                                }
                              }}
                            >
                              {t('notifications.reject', 'Reject')}
                            </Button>

                            {item.data.passengerId && (
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onClose();
                                  navigate(`/inbox/${item.data.passengerId}`);
                                }}
                                variant="outlined"
                                startIcon={<ChatIcon />}
                                sx={{
                                  py: 1.5,
                                  px: 4,
                                  fontSize: '1.25rem',
                                  fontWeight: 800,
                                  borderRadius: '16px',
                                  textTransform: 'none',
                                  borderColor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                                  color: 'text.primary',
                                  '&:hover': {
                                    borderColor: '#F59E0B',
                                    backgroundColor: 'rgba(245, 158, 11, 0.05)',
                                    transform: 'translateY(-2px)'
                                  }
                                }}
                              >
                                {t('notifications.chat', 'Chat')}
                              </Button>
                            )}
                          </>
                        )}
                      </Box>
                    )}

                    {/* Bid submission form for drivers on new ride requests */}
                    {isNewRideRequest && (
                      <Box sx={{ mt: 3, width: '100%', pl: { xs: 0, sm: 11 } }}>
                        {(() => {
                          const bidState = bidsState[item.id] || { price: '', message: '', submitting: false, success: false, error: '' };
                          
                          if (bidState.success) {
                            return (
                              <Box sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 2,
                                px: 3,
                                borderRadius: '16px',
                                backgroundColor: 'rgba(16, 185, 129, 0.15)',
                                color: '#10B981'
                              }}>
                                <CheckIcon sx={{ fontSize: '1.8rem' }} />
                                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                  {t('notifications.bidSubmitted', 'Bid Submitted Successfully!')}
                                </Typography>
                              </Box>
                            );
                          }

                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: '600px' }}>
                              {bidState.error && (
                                <Alert severity="error" sx={{ borderRadius: '12px' }}>
                                  {bidState.error}
                                </Alert>
                              )}
                              
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                                <Box sx={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
                                    {t('rideRequest.yourBid', 'Your Price Offer ($)')}
                                  </Typography>
                                  <input
                                    type="number"
                                    placeholder={t('rideRequest.pricePlaceholder', 'e.g. 50')}
                                    value={bidState.price}
                                    onChange={(e) => setBidsState(prev => ({
                                      ...prev,
                                      [item.id]: { ...(prev[item.id] || { price: '', message: '', submitting: false, success: false, error: '' }), price: e.target.value, error: '' }
                                    }))}
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={bidState.submitting}
                                    style={{
                                      padding: '12px 16px',
                                      fontSize: '1.15rem',
                                      borderRadius: '12px',
                                      border: '1px solid rgba(0,0,0,0.15)',
                                      backgroundColor: mode === 'dark' ? '#242424' : '#fff',
                                      color: mode === 'dark' ? '#fff' : '#000',
                                      outline: 'none',
                                      width: '100%',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </Box>

                                <Box sx={{ flex: '2 1 300px', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
                                    {t('rideRequest.messageToPassenger', 'Message (Optional)')}
                                  </Typography>
                                  <input
                                    type="text"
                                    placeholder={t('rideRequest.messagePlaceholder', 'e.g. I can pick you up in a clean SUV')}
                                    value={bidState.message}
                                    onChange={(e) => setBidsState(prev => ({
                                      ...prev,
                                      [item.id]: { ...(prev[item.id] || { price: '', message: '', submitting: false, success: false, error: '' }), message: e.target.value, error: '' }
                                    }))}
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={bidState.submitting}
                                    style={{
                                      padding: '12px 16px',
                                      fontSize: '1.15rem',
                                      borderRadius: '12px',
                                      border: '1px solid rgba(0,0,0,0.15)',
                                      backgroundColor: mode === 'dark' ? '#242424' : '#fff',
                                      color: mode === 'dark' ? '#fff' : '#000',
                                      outline: 'none',
                                      width: '100%',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                </Box>
                              </Box>

                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                  onClick={(e) => { e.stopPropagation(); handleNewRideRequestBid(item.id, item.data.requestId!); }}
                                  variant="contained"
                                  disabled={bidState.submitting || !bidState.price}
                                  startIcon={bidState.submitting ? <CircularProgress size={18} color="inherit" /> : <CarIcon />}
                                  sx={{
                                    py: 1.5,
                                    px: 4,
                                    fontSize: '1.15rem',
                                    fontWeight: 800,
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                                    boxShadow: '0 8px 16px rgba(245, 158, 11, 0.25)',
                                    color: 'white',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #D97706 0%, #B45309 100%)',
                                      transform: 'translateY(-2px)'
                                    }
                                  }}
                                >
                                  {t('rideRequest.submitBid', 'Submit Bid')}
                                </Button>

                                {item.data.passengerId && (
                                  <Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onClose();
                                      navigate(`/inbox/${item.data.passengerId}`);
                                    }}
                                    variant="outlined"
                                    startIcon={<ChatIcon />}
                                    sx={{
                                      py: 1.5,
                                      px: 4,
                                      fontSize: '1.15rem',
                                      fontWeight: 800,
                                      borderRadius: '12px',
                                      textTransform: 'none',
                                      borderColor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                                      color: 'text.primary',
                                      '&:hover': {
                                        borderColor: '#F59E0B',
                                        backgroundColor: 'rgba(245, 158, 11, 0.05)',
                                        transform: 'translateY(-2px)'
                                      }
                                    }}
                                  >
                                    {t('notifications.chat', 'Chat')}
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          );
                        })()}
                      </Box>
                    )}
                  </ListItem>
                </Paper>
              );
            })}
          </List>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ p: 4, gap: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          fullWidth
          sx={{
            py: 2,
            fontSize: '1.5rem',
            fontWeight: 900,
            borderRadius: '20px',
            textTransform: 'none',
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            boxShadow: '0 10px 15px -3px rgba(245, 158, 11, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #D97706 0%, #B45309 100%)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          {t('alarmModal.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlarmModal;
